/** @format */
import { ChangeDetectionStrategy, Component, computed, inject, input, ViewEncapsulation } from '@angular/core';
import { getRuleValidationResult, CourseUnitRuleValidationResult, PlanValidationResult, ValidatablePlan } from 'common-typescript';
import { CourseUnit, CourseUnitRule, EntityWithRule, Rule } from 'common-typescript/types';
import { RULE_SERVICE } from '../../../ajs-upgraded-modules';
import { PLAN_ACTIONS_SERVICE_INJECTION_TOKEN, PlanActionsService } from '../../../plan/plan-actions-service/plan-actions.service';
import { PlanRuleData } from '../../../service/plan-rule-data.service';
import { PlanRuleSelectionService } from '../../../service/plan-rule-selection.service';
import { PlanData, PlanStateObject } from '../../../service/plan-state.service';

@Component({
    selector: 'sis-plan-structure-course-unit-rule',
    templateUrl: './plan-structure-course-unit-rule.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class PlanStructureCourseUnitRuleComponent {
    rule = input.required<CourseUnitRule>();
    parentModule = input.required<EntityWithRule>();
    planValidationResult = input.required<PlanValidationResult>();
    planStateObject = input.required<PlanStateObject>();
    planRuleData = input.required<PlanRuleData>();
    validatablePlan = input.required<ValidatablePlan>();
    selectionUIState = input<'ACTIVE' | 'DISABLED' | 'SELECTABLE'>('ACTIVE');
    planData = input.required<PlanData>();
    ruleDepthLevel = input.required<number>();
    headingLevel = input.required<number>();

    ruleService = inject(RULE_SERVICE);
    planRuleSelectionService: PlanRuleSelectionService = inject(PlanRuleSelectionService);
    planActionsService: PlanActionsService = inject(PLAN_ACTIONS_SERVICE_INJECTION_TOKEN);

    ruleValidationResult = computed(this.ruleValidationResultComputation());
    ruleCourseUnit = computed(this.ruleCourseUnitComputation());
    parentRule = computed<Rule | null>(this.parentRuleComputation());
    isParentRuleSelectAllRule = computed<boolean>(this.isParentRuleSelectAllRuleComputation());
    isParentSelectOneRule = computed<boolean>(this.isSelectOneRuleComputation());

    isRuleSelectionDisabled = computed(this.isRuleSelectionDisabledComputation());
    isImplicit = computed(this.isImplicitComputation());
    showMandatorySelection = computed(this.showMandatorySelectionComputation());

    action = computed(this.actionComputation());
    actionInputType = computed(this.actionInputTypeComputation());
    actionInputName = computed(this.actionInputNameComputation());

    handleToggle(): void {
        const active = this.ruleValidationResult().active;
        const courseUnit = this.ruleCourseUnit();
        const parentModule = this.parentModule();
        const validatablePlan = this.validatablePlan();
        const planStateObject = this.planStateObject();
        if (
            this.planRuleSelectionService.isImplicit(courseUnit, parentModule, planStateObject) &&
            this.planRuleSelectionService.canImplicitlySelectedCourseUnitBeMovedToTargetModule(
                courseUnit,
                parentModule,
                validatablePlan,
                planStateObject,
            )
        ) {
            this.planActionsService?.moveCourseUnit(courseUnit, parentModule);
        } else if (!active) {
            this.planActionsService?.selectCourseUnit(courseUnit, parentModule);
        } else {
            this.planActionsService?.removeCourseUnit(courseUnit, parentModule);
        }
    }

    ruleValidationResultComputation(): () => CourseUnitRuleValidationResult {
        return () => getRuleValidationResult(this.planValidationResult(), this.parentModule().id, this.rule());
    }

    ruleCourseUnitComputation(): () => CourseUnit {
        return () => this.planRuleData().ruleCourseUnitVersionsByGroupId[this.rule().courseUnitGroupId];
    }

    parentRuleComputation(): () => Rule | null {
        return () => this.ruleService.getParentRule(this.parentModule().rule, this.rule());
    }

    isParentRuleSelectAllRuleComputation(): () => boolean {
        return () => this.ruleService.isSelectAllRule(this.parentRule());
    }

    isRuleSelectionDisabledComputation(): () => boolean {
        return () =>
            this.selectionUIState() === 'DISABLED' ||
            (this.isParentRuleSelectAllRule() && this.ruleValidationResult()?.active) ||
            this.planRuleSelectionService.isCourseUnitSelectionDisabled(
                this.ruleCourseUnit(),
                this.parentModule(),
                this.validatablePlan(),
                this.planStateObject(),
            );
    }

    isImplicitComputation(): () => boolean {
        return () => this.planRuleSelectionService.isImplicit(this.ruleCourseUnit(), this.parentModule(), this.planStateObject());
    }

    isSelectOneRuleComputation(): () => boolean {
        return () => this.ruleService.isSelectOneRule(this.parentRule());
    }

    actionComputation() {
        return () => (this.isRuleSelectionDisabled() ? undefined : ('select-to-plan' as const));
    }

    actionInputTypeComputation() {
        return () => (this.isParentSelectOneRule() ? 'radio-button' : 'checkbox');
    }

    actionInputNameComputation() {
        return () => (this.parentRule() ? this.parentRule().localId : this.rule().localId);
    }

    showMandatorySelectionComputation() {
        return () => this.ruleValidationResult()?.active && (this.isParentRuleSelectAllRule() || !this.parentRule());
    }
}
